import { backgroundFills, mainStroke, polygonTraps } from "./drawingShapeParts";

// Under this run the base of a channel is upright: the other line is apart sideways, not above it
const MIN_BASE_RUN_PX = 1;
const WIDTH_POINT_INDEX = 2;

const isUpright = (first, second) => Math.abs(second.x - first.x) < MIN_BASE_RUN_PX;

// The place on the base under `x`
const baseYAt = (first, second, x) => first.y + ((x - first.x) * (second.y - first.y)) / (second.x - first.x);

// How far the line through `through` lies from the base, parallel to it
const channelShift = (first, second, through) =>
  isUpright(first, second)
    ? { x: through.x - first.x, y: 0 }
    : { x: 0, y: through.y - baseYAt(first, second, through.x) };

const middleOf = (first, second) => ({ x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 });

/**
 * Parallel Channel (po-chart-visual-spec.md §7.5): the base between the first
 * two points, the other line through the third one, no sides and no middle line,
 * Background between them. See TOOLS in drawingTools.js.
 *
 * @param {Array<{ x: number, y: number }>} points - CSS px of the pane
 * @param {{ width: number, height: number }} pane
 * @param {object} style - BACKGROUND_STYLE with colour and width
 * @returns {{ strokes: object[], fills: object[] }}
 */
export const parallelChannelShape = ([first, second, through], pane, style) => {
  const shift = channelShift(first, second, through);
  const otherFirst = { x: first.x + shift.x, y: first.y + shift.y };
  const otherSecond = { x: second.x + shift.x, y: second.y + shift.y };

  return {
    strokes: [
      mainStroke(style, [
        [first.x, first.y, second.x, second.y],
        [otherFirst.x, otherFirst.y, otherSecond.x, otherSecond.y],
      ]),
    ],
    fills: backgroundFills(style, polygonTraps([first, second, otherSecond, otherFirst])),
  };
};

/**
 * Handles of a channel: on the points of the base and in the middle of the other line.
 *
 * @param {Array<{ x: number, y: number }>} points - CSS px of the pane
 * @returns {Array<{ x: number, y: number }>}
 */
export const parallelChannelHandles = ([first, second, through]) => {
  const shift = channelShift(first, second, through);
  const middle = middleOf(first, second);

  return [first, second, { x: middle.x + shift.x, y: middle.y + shift.y }];
};

/**
 * Where the point of a dragged handle goes. The third handle changes only the
 * width: its point stays over the middle of the base, whatever the pointer does along the lines.
 *
 * @param {Array<{ x: number, y: number }>} points - CSS px of the pane
 * @param {number} pointIndex - of the handle
 * @param {{ x: number, y: number }} pointer
 * @returns {{ x: number, y: number }}
 */
export const parallelChannelDragTarget = ([first, second], pointIndex, pointer) => {
  if (pointIndex !== WIDTH_POINT_INDEX) return pointer;

  const middle = middleOf(first, second);

  return isUpright(first, second)
    ? { x: pointer.x, y: middle.y }
    : { x: middle.x, y: middle.y + pointer.y - baseYAt(first, second, pointer.x) };
};
